/**
 * Phoenix Channels socket — single shared connection used by presence
 * (`user:<id>`) and any future user-scoped channels.
 *
 * Holds one open socket per page lifetime. Reused across StrictMode double
 * mounts and Vite hot-reload so we don't multiply connections.
 *
 * Token: fetched from `/api/token` on the Phoenix server (per existing
 * meetings flow). Falls back to the env-configured URL so dev "just works"
 * once VITE_PHOENIX_SOCKET_URL is set.
 */

import { Socket, Channel } from 'phoenix'
import { getDeviceId } from './device-id'
import { setSocketAuthToken } from './phoenix-auth-token'
import { getSdkSessionToken } from './sdk-session-token'

type SocketState = 'idle' | 'connecting' | 'open' | 'closed'

interface SocketBundle {
  socket: Socket
  state: SocketState
}

const SOCKET_URL =
  import.meta.env.VITE_PHOENIX_SOCKET_URL ||
  // Browser fallback only — server-side rendering shouldn't reach this.
  (typeof window !== 'undefined'
    ? window.location.protocol === 'https:'
      ? 'wss://signal.instavc.ai/socket'
      : 'ws://localhost:4000/socket'
    : '')

const TOKEN_ENDPOINT = '/api/token'

// Validation logging (O-presence): surface how the Phoenix endpoints resolve so
// a misconfigured production build is obvious in the browser console. Logs the
// resolved socket URL once at module load and warns loudly on the localhost
// fallback, which means VITE_PHOENIX_SOCKET_URL was missing from the build.
{
  const envSocket = import.meta.env.VITE_PHOENIX_SOCKET_URL
   
  console.info(
    `[phoenix] socket URL resolved to ${SOCKET_URL} ` +
      `(VITE_PHOENIX_SOCKET_URL ${envSocket ? 'set' : 'MISSING → fallback'})`,
  )
  if (!envSocket && SOCKET_URL.includes('localhost')) {
     
    console.warn(
      '[phoenix] using localhost socket fallback — presence will not work in ' +
        'production. Set VITE_PHOENIX_SOCKET_URL in the build env.',
    )
  }
}

let bundle: SocketBundle | null = null
let tokenPromise: Promise<string> | null = null
// In-flight socket build. Cached so concurrent callers (presence + messenger +
// StrictMode double-mount + re-renders) share ONE socket instead of each racing
// past the `bundle` guard and constructing a separate WebSocket. Without this,
// N simultaneous getPhoenixSocket() calls opened N connections.
let socketPromise: Promise<Socket> | null = null

function getInternalJwt(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') ||
      sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } }
    const s = parsed?.session
    if (s?.expiresAt && s.expiresAt <= Date.now()) return ''
    return s?.accessToken || ''
  } catch {
    return ''
  }
}

/**
 * Decode the `tenantId` claim from the app's access-token JWT.
 *
 * The signaling server uses `user_id` + `tenant_id` to resolve the SAME
 * per-org `contactPresenceId` that chat/presence key on (so a message sent from
 * this browser and its presence topic line up server-side). The token `sub`
 * stays the auth user id (meetings), so we pass tenant_id alongside it here.
 * Best-effort — returns null if the token is missing/opaque/malformed.
 */
function getTenantIdFromJwt(): string | null {
  try {
    const jwt = getInternalJwt()
    if (!jwt) return null
    const b64 = jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/')
    if (!b64) return null
    const payload = JSON.parse(atob(b64)) as Record<string, unknown>
    const tenantId = payload.tenantId ?? payload.tenant_id
    return typeof tenantId === 'string' && tenantId ? tenantId : null
  } catch {
    return null
  }
}

/**
 * Exchange the Node-issued JWT (sub = userId) for a Phoenix RS256 token
 * the signaling server's UserSocket accepts. Cached for the page lifetime;
 * cleared on `disconnectPhoenixSocket()`.
 */
async function fetchPhoenixToken(userId: string, userName?: string): Promise<string> {
  if (tokenPromise) return tokenPromise
  tokenPromise = (async () => {
    const internalToken = getInternalJwt()
    if (!internalToken) throw new Error('not authenticated')

    // Phase 2: prefer the SDK session token (one token for socket + REST, no
    // inMeet dependency). It's minted by sdk-server and signaling accepts it
    // unchanged (same RS256 keypair). If the exchange is unavailable, fall
    // through to the legacy /api/token mint below — so the socket can never
    // fail to connect because of the swap.
    try {
      const sdkToken = await getSdkSessionToken()
      if (sdkToken) return sdkToken
    } catch {
      /* fall back to /api/token */
    }

    // Node backend proxies /api/token via the same /api prefix that
    // platform/signaling/index.ts already uses. The fetch goes direct to
    // the Phoenix server, NOT the Node backend.
    const envHttp = import.meta.env.VITE_PHOENIX_HTTP_URL
    const phoenixBase = envHttp || 'http://localhost:4000'
    const tokenUrl = `${phoenixBase}${TOKEN_ENDPOINT}`

    // Validation logging (O-presence): confirm which base the /api/token call
    // uses. A localhost URL on an https page is the exact prod misconfig
    // symptom — warn so it stands out in the browser console.
     
    console.info(
      `[phoenix] fetching token from ${tokenUrl} ` +
        `(VITE_PHOENIX_HTTP_URL ${envHttp ? 'set' : 'MISSING → fallback'})`,
    )
    if (!envHttp) {
       
      console.warn(
        '[phoenix] /api/token base fell back to http://localhost:4000 — this ' +
          'fails in production (localhost + mixed content). Set ' +
          'VITE_PHOENIX_HTTP_URL in the build env.',
      )
    }

    // tenant_id lets the signaling server resolve this user's per-org
    // contactPresenceId (which chat + presence key on). Sourced from the app JWT.
    const tenantId = getTenantIdFromJwt()

    const res = await fetch(tokenUrl, {
      method: 'POST',
      // x-skip-network-error opts out of the global Connection Lost overlay —
      // presence is non-essential, so a transient Phoenix outage shouldn't
      // black out the app.
      headers: { 'Content-Type': 'application/json', 'x-skip-network-error': '1' },
      body: JSON.stringify({ user_id: userId, user_name: userName, tenant_id: tenantId }),
    })
    if (!res.ok) {
      tokenPromise = null
      throw new Error(`phoenix /api/token failed: ${res.status}`)
    }
    const data = (await res.json()) as { token: string }
    return data.token
  })()
  return tokenPromise.catch((err) => {
    // Clear the promise so a future attempt can re-fetch — and also propagate
    // for the caller's local catch handler.
    tokenPromise = null
    throw err
  })
}

/**
 * Open (or return) the shared Phoenix socket for the given user.
 * Idempotent: repeated calls return the same instance while still open.
 */
export async function getPhoenixSocket(
  userId: string,
  userName?: string,
): Promise<Socket> {
  // Fast path — a live socket already exists.
  if (bundle && bundle.state !== 'closed') return bundle.socket

  // A previous socket closed; drop it (and any stale build promise) so we
  // rebuild cleanly on reconnect.
  if (bundle && bundle.state === 'closed') {
    bundle = null
    socketPromise = null
  }

  // A build is already in flight — every concurrent caller awaits the SAME
  // promise, so only one WebSocket is ever constructed.
  if (socketPromise) return socketPromise

  socketPromise = (async () => {
    // Prime the token cache so the first connect has one synchronously available
    // (phoenix-js reads `authToken` when it opens each connection, and cannot
    // await). Throwing here is what signals "not authenticated" to callers.
    const token = await fetchPhoenixToken(userId, userName)

    // Keep the token fresh in the BACKGROUND so a reconnect presents a valid one.
    // Without this, the token captured at construction is reused for the life of
    // the page: once it expires (SDK session token TTL ~1h) every reconnect is
    // rejected by UserSocket, and presence + live chat stay dead until a manual
    // reload.
    //
    // Goes through `fetchPhoenixToken` (not `getSdkSessionToken` directly) to keep
    // the legacy `/api/token` fallback — clearing `tokenPromise` first, since that
    // cache is page-lifetime by design.
    const refreshToken = () => {
      tokenPromise = null
      void fetchPhoenixToken(userId, userName)
        .then((t) => { if (t) setSocketAuthToken(socket, t) })
        .catch(() => { /* keep the previous token; the next close retries */ })
    }

    const socket = new Socket(SOCKET_URL, {
      // The token travels in the `Sec-WebSocket-Protocol` header, never the URL
      // (see phoenix-auth-token.ts). phoenix-js re-reads `authToken` on every
      // (re)connect, so `refreshToken` swaps a fresh one in place.
      authToken: token,
      // `device_id` → presence meta, so push fan-out can skip this exact device.
      params: () => ({ device_id: getDeviceId() }),
      // Exponential backoff up to 60s (O13 — Web_Client reconnect).
      reconnectAfterMs: (tries: number) => Math.min(1000 * Math.pow(2, tries), 60_000),
      // Heartbeat — keep alive through aggressive corporate proxies.
      heartbeatIntervalMs: 30_000,
      logger: (kind, msg, data) => {
        if (import.meta.env.DEV) {
           
          console.debug(`[phoenix] ${kind} ${msg}`, data ?? '')
        }
      },
    })

    bundle = { socket, state: 'connecting' }
    socket.onOpen(() => { if (bundle) bundle.state = 'open' })
    socket.onClose(() => {
      if (bundle) bundle.state = 'closed'
      // Re-mint before phoenix-js's next backoff attempt reads `params` again. An
      // expired token is the most likely reason a healthy socket closed.
      refreshToken()
    })
    socket.onError(() => refreshToken())

    socket.connect()
    return socket
  })().catch((err) => {
    // Failed build (e.g. token fetch) — clear so a later call can retry.
    socketPromise = null
    throw err
  })

  return socketPromise
}

/**
 * Join (or reuse) a channel. `onMessage` and similar wiring happens on the
 * returned `Channel`. We *do not* memoize Channel objects globally because
 * a channel is cheap to construct and per-component wiring keeps lifecycle
 * cleaner.
 */
export async function joinUserPresenceChannel(
  userId: string,
  targetUserId: string,
  userName?: string,
): Promise<Channel> {
  const socket = await getPhoenixSocket(userId, userName)
  const channel = socket.channel(`user:${targetUserId}`, {})
  await new Promise<void>((resolve, reject) => {
    channel
      .join()
      .receive('ok', () => resolve())
      .receive('error', (err) => reject(new Error(`join failed: ${JSON.stringify(err)}`)))
      .receive('timeout', () => reject(new Error('join timeout')))
  })
  return channel
}

/** Tear down the shared socket — call on logout or HMR teardown. */
export function disconnectPhoenixSocket(): void {
  if (bundle?.socket) {
    bundle.socket.disconnect()
  }
  bundle = null
  tokenPromise = null
  socketPromise = null
}

// Vite HMR — cleanly teardown on module reload so we don't stack sockets.
if (import.meta.hot) {
  import.meta.hot.dispose(() => disconnectPhoenixSocket())
}
